import { format, parse } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import type { GetEventForBuilderByIdQuery } from 'types/generated/client';
import { convertToMinutesAfterMidnight } from 'utils/shared/time/convertToMinutesAfterMidnight';
import { getTimezone } from 'utils/shared/time/getTimezone';

export type Nullable<T> = { [K in keyof T]: T[K] | null };

export interface HourOption {
  display12: string;
  display24: string;
  minutesAfterMidnight: number;
  id: number;
}

function generateHourOptions(intervalMinutes: number): HourOption[] {
  const options: HourOption[] = [];
  const totalMinutesInDay = 1440;

  for (
    let minutesAfterMidnight = 0;
    minutesAfterMidnight < totalMinutesInDay;
    minutesAfterMidnight += intervalMinutes
  ) {
    const hours = Math.floor(minutesAfterMidnight / 60);
    const minutes = minutesAfterMidnight % 60;
    const display12 = `${((hours + 11) % 12) + 1}:${minutes.toString().padStart(2, '0')} ${
      hours >= 12 ? 'PM' : 'AM'
    }`;
    const display24 = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

    options.push({ display12, display24, minutesAfterMidnight, id: minutesAfterMidnight });
  }

  return options;
}

export const timeSlotHourOptions = generateHourOptions(15);

/**
 * Converts a date + time (interpreted as local in the given timezone) to UTC ISO string.
 * Used for event group time slots where the form displays times in the venue timezone.
 */
export function convertTimeSlotToUtc(params: {
  date: Date;
  time: string;
  timezone?: string | null;
}): string {
  const tz = getTimezone(params.timezone);
  const timestampLocal = `${format(params.date, 'yyyy-MM-dd')}T${params.time}:00`;
  const dateInZone = parse(timestampLocal, "yyyy-MM-dd'T'HH:mm:ss", new Date());
  const utcDate = fromZonedTime(dateInZone, tz);
  return formatInTimeZone(utcDate, 'UTC', "yyyy-MM-dd'T'HH:mm:ssXXX");
}

type BuilderEventGroup = NonNullable<GetEventForBuilderByIdQuery['eventsByPk']>['groups'][number];

export type EventGroupForTimeSlots = Pick<BuilderEventGroup, 'timeSlots' | 'startsAt' | 'endsAt'>;

export function convertNullsToUndefined<T>(obj: Nullable<T>): T {
  const result: Record<string, unknown> = {};
  for (const key in obj) {
    if (obj[key] === null) {
      result[key] = undefined;
    } else {
      result[key] = obj[key];
    }
  }
  return result as T;
}

export type MoneyBallReward = { title: string; amount: number };

export function parseMoneyBallRewards(value: unknown): MoneyBallReward[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (r): r is MoneyBallReward =>
        r != null &&
        typeof (r as MoneyBallReward).title === 'string' &&
        typeof (r as MoneyBallReward).amount === 'number',
    )
    .map((r) => ({ title: r.title, amount: Number(r.amount) }));
}

// DB defaults desired_number_of_pools to 1; the form schema requires >= 2, so treat < 2 as "no pools"
export function parseDesiredPools(desiredNumberOfPools: number | null | undefined): {
  useMultiplePools: boolean;
  desiredNumberOfPools: number | undefined;
} {
  const hasPools = (desiredNumberOfPools ?? 0) > 1;
  return {
    useMultiplePools: hasPools,
    desiredNumberOfPools: hasPools ? desiredNumberOfPools ?? undefined : undefined,
  };
}

export type TimeSlotFormItem = { date: Date; from: number; to: number };

/**
 * Parses time slots from an event group for the form.
 * Priority (backward compatible):
 * 1. Use event_group_time_slots (timeSlots) when non-empty
 * 2. Fallback to event_groups.startsAt/endsAt for legacy groups
 */
export function parseTimeSlotsFromGroup(
  group: Partial<EventGroupForTimeSlots> | null | undefined,
  timezone?: string | null,
): TimeSlotFormItem[] {
  if (!group) return [{ date: new Date(), from: 480, to: 1260 }];
  const slots = group.timeSlots;
  if (Array.isArray(slots) && slots.length > 0) {
    return slots
      .filter((s) => s?.startsAt && s?.endsAt)
      .map((s) => ({
        date: new Date(s.startsAt!),
        from: convertToMinutesAfterMidnight({
          timestamp: s.startsAt!,
          timezone: timezone ?? undefined,
        }),
        to: convertToMinutesAfterMidnight({
          timestamp: s.endsAt!,
          timezone: timezone ?? undefined,
        }),
      }));
  }
  if (group?.startsAt && group?.endsAt) {
    return [
      {
        date: new Date(group.startsAt),
        from: convertToMinutesAfterMidnight({
          timestamp: group.startsAt,
          timezone: timezone ?? undefined,
        }),
        to: convertToMinutesAfterMidnight({
          timestamp: group.endsAt,
          timezone: timezone ?? undefined,
        }),
      },
    ];
  }
  return [{ date: new Date(), from: 480, to: 1260 }];
}

/**
 * Returns effective startsAt/endsAt for a group.
 * Priority (backward compatible):
 * 1. Use event_group_time_slots: first slot's startsAt, last slot's endsAt
 * 2. Fallback to event_groups.startsAt/endsAt for legacy groups
 */
export function getGroupEffectiveTimeRange(
  group: Partial<EventGroupForTimeSlots> | null | undefined,
): {
  startsAt: string | null;
  endsAt: string | null;
} {
  if (!group) return { startsAt: null, endsAt: null };
  const slots = group.timeSlots;
  if (Array.isArray(slots) && slots.length > 0) {
    const validSlots = slots.filter((s) => s?.startsAt && s?.endsAt);
    if (validSlots.length > 0) {
      const first = validSlots[0];
      const last = validSlots[validSlots.length - 1];
      return {
        startsAt: first.startsAt ?? null,
        endsAt: last.endsAt ?? null,
      };
    }
  }
  return {
    startsAt: group.startsAt ?? null,
    endsAt: group.endsAt ?? null,
  };
}
